import * as React from 'react'

import { UseFormReturn, useFieldArray, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  ErrorText,
  Flex,
  Grid,
  IconButton,
  Separator,
  Stack,
  TertiaryButton,
  Text,
} from '@enechain/ecloud-designsystem'
import { FaPlusRegular, FaXmarkRegular } from '@enechain/ecloud-icons'
import { DeliveryPeriodType } from '@enechain/eposition-proto/types/v1/deal_pb'
import { Card } from '@enechain/eposition-ui'

import { useSyncLegs } from '~/pages/Simulation/hooks/useSyncLegs'
import { OrderSimulationSchema } from '~/pages/Simulation/schemas'

import { SimulationLegForm } from './LegForm'

type Props = {
  formMethods: UseFormReturn<OrderSimulationSchema>
  index: number
}

export const LegInputSection: React.FC<Props> = ({ formMethods, index }) => {
  const { t } = useTranslation(['page', 'domain', 'common'])

  const watchedIsSpreadTrade = useWatch({
    name: `orderPattern.${index}.spreadType.isSpreadTrade`,
    control: formMethods.control,
  })
  const watchedBaseDeliveryPeriodType = useWatch({
    name: `orderPattern.${index}.baseSummaryOrder.deliveryPeriodType`,
    control: formMethods.control,
  })
  const watchedExchangeDeliveryPeriodType = useWatch({
    name: `orderPattern.${index}.exchangeSummaryOrder.deliveryPeriodType`,
    control: formMethods.control,
  })
  const watchedBaseAreaCode = useWatch({
    name: `orderPattern.${index}.baseSummaryOrder.areaCode`,
    control: formMethods.control,
  })
  const watchedExchangeAreaCode = useWatch({
    name: `orderPattern.${index}.exchangeSummaryOrder.areaCode`,
    control: formMethods.control,
  })
  const isPhysicalFuture = useWatch({
    name: `orderPattern.${index}.isPhysicalFuture`,
    control: formMethods.control,
  })

  const {
    fields: baseFields,
    append: appendBaseTransaction,
    remove: removeBaseTransaction,
  } = useFieldArray({
    control: formMethods.control,
    name: `orderPattern.${index}.baseTransactions`,
  })

  const {
    fields: exchangeFields,
    append: appendExchangeTransaction,
    remove: removeExchangeTransaction,
  } = useFieldArray({
    control: formMethods.control,
    name: `orderPattern.${index}.exchangeTransactions`,
  })

  const handleAppendBaseTransaction = (): void => {
    const currentBaseSummary = formMethods.getValues(
      `orderPattern.${index}.baseSummaryOrder`,
    )
    appendBaseTransaction({
      ...currentBaseSummary,
      deliveryPeriodType: DeliveryPeriodType.MONTH,
      deliveryDateRange: {
        deliveryStartDate: undefined,
        deliveryEndDate: undefined,
      },
    })
  }

  const handleAppendExchangeTransaction = (): void => {
    const currentExchangeSummary = formMethods.getValues(
      `orderPattern.${index}.exchangeSummaryOrder`,
    )

    if (currentExchangeSummary === undefined) {
      return
    }

    appendExchangeTransaction({
      ...currentExchangeSummary,
      deliveryPeriodType: DeliveryPeriodType.MONTH,
      deliveryDateRange: {
        deliveryStartDate: undefined,
        deliveryEndDate: undefined,
      },
    })
  }
  useSyncLegs({ formMethods, index })

  const errors = formMethods.formState.errors
  const baseTransactionsError = errors.orderPattern?.[index]
    ?.baseTransactions as { message?: string } | undefined
  const exchangeTransactionsError = errors.orderPattern?.[index]
    ?.exchangeTransactions as { message?: string } | undefined

  const isBaseSplitDateRange =
    watchedBaseDeliveryPeriodType === DeliveryPeriodType.FISCAL_YEAR ||
    watchedBaseDeliveryPeriodType === DeliveryPeriodType.MULTI_MONTH
  const isExchangeSplitDateRange =
    watchedExchangeDeliveryPeriodType === DeliveryPeriodType.FISCAL_YEAR ||
    watchedExchangeDeliveryPeriodType === DeliveryPeriodType.MULTI_MONTH

  return (
    <Card>
      {watchedIsSpreadTrade ? (
        <Grid gridGap="4" gridTemplateColumns="1fr 1fr">
          <Stack gap="2">
            <Card backgroundColor="Surface.secondary">
              <Stack gap="4" alignItems="center" width="full">
                <Text textStyle="headerXs" textAlign="start" width="full">
                  {t('page:simulation.base_side')}
                </Text>
                {baseFields.map((field, legIndex) => (
                  <Card
                    key={field.id}
                    backgroundColor="Surface.primary"
                    width="full"
                  >
                    <Stack gap="4">
                      <Flex justifyContent="space-between" alignItems="center">
                        <Text textStyle="headerSm">
                          {t('page:simulation.input_left', {
                            value: legIndex + 1,
                          })}
                        </Text>
                        <IconButton
                          aria-label="delete leg"
                          onClick={() => removeBaseTransaction(legIndex)}
                        >
                          <FaXmarkRegular />
                        </IconButton>
                      </Flex>
                      <Separator />
                      <SimulationLegForm
                        schemaKeyName={`orderPattern.${index}.baseTransactions.${legIndex}`}
                        control={formMethods.control}
                        areaCode={watchedBaseAreaCode}
                        isPhysicalFuture={isPhysicalFuture}
                      />
                    </Stack>
                  </Card>
                ))}
                {isBaseSplitDateRange && (
                  <TertiaryButton
                    leftIcon={<FaPlusRegular />}
                    onClick={handleAppendBaseTransaction}
                  >
                    {t('common:action.neutral.add', {
                      value: t('domain:model.posting.column.leg'),
                    })}
                  </TertiaryButton>
                )}
              </Stack>
            </Card>
            {baseTransactionsError?.message !== undefined && (
              <ErrorText>{baseTransactionsError.message}</ErrorText>
            )}
          </Stack>
          <Stack gap="2">
            <Card backgroundColor="Surface.secondary">
              <Stack gap="4" alignItems="center" width="full">
                <Text textStyle="headerXs" textAlign="start" width="full">
                  {t('page:simulation.exchange_side')}
                </Text>
                {exchangeFields.map((field, legIndex) => (
                  <Card
                    key={field.id}
                    backgroundColor="Surface.primary"
                    width="full"
                  >
                    <Stack gap="4">
                      <Flex justifyContent="space-between" alignItems="center">
                        <Text textStyle="headerSm">
                          {t('page:simulation.input_right', {
                            value: legIndex + 1,
                          })}
                        </Text>
                        <IconButton
                          aria-label="delete leg"
                          onClick={() => removeExchangeTransaction(legIndex)}
                        >
                          <FaXmarkRegular />
                        </IconButton>
                      </Flex>
                      <Separator />
                      <SimulationLegForm
                        control={formMethods.control}
                        schemaKeyName={`orderPattern.${index}.exchangeTransactions.${legIndex}`}
                        areaCode={watchedExchangeAreaCode}
                        isPhysicalFuture={isPhysicalFuture}
                      />
                    </Stack>
                  </Card>
                ))}
                {isExchangeSplitDateRange && (
                  <TertiaryButton
                    leftIcon={<FaPlusRegular />}
                    onClick={handleAppendExchangeTransaction}
                  >
                    {t('common:action.neutral.add', {
                      value: t('domain:model.posting.column.leg'),
                    })}
                  </TertiaryButton>
                )}
              </Stack>
            </Card>
            {exchangeTransactionsError?.message !== undefined && (
              <ErrorText>{exchangeTransactionsError.message}</ErrorText>
            )}
          </Stack>
        </Grid>
      ) : (
        <Stack gap="2">
          <Card backgroundColor="Surface.secondary">
            <Stack gap="4" alignItems="center" width="full">
              {baseFields.map((field, legIndex) => (
                <Card
                  key={field.id}
                  backgroundColor="Surface.primary"
                  width="full"
                >
                  <Stack gap="4">
                    <Flex justifyContent="space-between" alignItems="center">
                      <Text textStyle="headerSm">
                        {t('page:simulation.input_index', {
                          value: legIndex + 1,
                        })}
                      </Text>
                      <IconButton
                        aria-label="delete leg"
                        onClick={() => removeBaseTransaction(legIndex)}
                      >
                        <FaXmarkRegular />
                      </IconButton>
                    </Flex>
                    <Separator />
                    <SimulationLegForm
                      control={formMethods.control}
                      schemaKeyName={`orderPattern.${index}.baseTransactions.${legIndex}`}
                      areaCode={watchedBaseAreaCode}
                      isPhysicalFuture={isPhysicalFuture}
                    />
                  </Stack>
                </Card>
              ))}
              <TertiaryButton
                leftIcon={<FaPlusRegular />}
                onClick={handleAppendBaseTransaction}
              >
                {t('common:action.neutral.add', {
                  value: t('domain:model.posting.column.leg'),
                })}
              </TertiaryButton>
            </Stack>
          </Card>
          {baseTransactionsError?.message !== undefined && (
            <ErrorText>{baseTransactionsError.message}</ErrorText>
          )}
        </Stack>
      )}
    </Card>
  )
}
